import { useState, useEffect } from 'react';
import { fabric } from 'fabric';
import { Settings } from '../../../../shared/constants';
import { getStitchingManipulationRulesFromActiveSelection } from '../../../../shared/Helpers';

function useCanvasEvents(
  canvasEl: Nullable<fabric.Canvas>,
  objectModifiedHandler: (activeObjects: fabric.Object[], skipSmallMovements: boolean) => void,
  onObjectSelected?: (isAnyActive: boolean, areAllActive: boolean) => void,
  onImageSelected?: ((obj: fabric.Object) => void) | null
) {
  const [isDragging, setIsDragging] = useState(false);
  const [selection, setSelection] = useState(false);
  const [lastPosX, setLastPosX] = useState(0);
  const [lastPosY, setLastPosY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  useEffect(() => {
    if (!canvasEl) return;

    canvasEl.defaultCursor = isPanning ? 'grabbing' : 'grab';
    canvasEl.selectionColor = 'transparent';
    canvasEl.selectionBorderColor = 'transparent';

    canvasEl
      .on('object:modified', e => {
        if (e.target) {
          const activeObjects = canvasEl.getActiveObjects();

          objectModifiedHandler(activeObjects, false);
        }
      })
      .on('mouse:wheel', opt => {
        const event = opt.e as WheelEvent;
        const delta = event.deltaY > 0 ? 20 : -20;
        let zoom = canvasEl.getZoom();
        zoom = zoom - delta / 1000;
        if (zoom > 10) zoom = 10;
        if (zoom < 0.01) zoom = 0.01;
        canvasEl!.zoomToPoint(new fabric.Point(event.offsetX, event.offsetY), zoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();
      })
      .on('mouse:down', opt => {
        if (opt.target) {
          if (opt.target.name && onImageSelected) {
            onImageSelected(opt.target);
          }
          canvasEl.selection = true;
        } else {
          canvasEl.selection = false;
        }

        const event = opt.e as WheelEvent;
        if (event.ctrlKey) {
          setSelection(false);
          setLastPosX(event.clientX);
          setLastPosY(event.clientY);
        }
        setIsPanning(true);
      })
      .on('mouse:move', opt => {
        if (isDragging) {
          const event = opt.e as WheelEvent;
          canvasEl.viewportTransform![4] += event.clientX - lastPosX;
          canvasEl.viewportTransform![5] += event.clientY - lastPosY;
          canvasEl.requestRenderAll();
          setLastPosX(event.clientX);
          setLastPosY(event.clientY);
        }

        if (isPanning && opt && opt.e && !canvasEl.getActiveObject()) {
          const mouseEvent = opt.e as MouseEvent;
          var delta = new fabric.Point(mouseEvent.movementX, mouseEvent.movementY);
          canvasEl.relativePan(delta);
        }
      })
      .on('mouse:up', () => {
        setIsDragging(false);
        setSelection(true);
        setIsPanning(false);
      })
      .on('selection:created', opt => {
        const activeObjects = canvasEl.getActiveObjects();
        if (activeObjects.length) {
          if (activeObjects.length > 1) {
            const activeSelection = opt.target as fabric.ActiveSelection;
            populateGroupLockFields(activeObjects, activeSelection);
          }
          activeObjects.forEach(element => {
            element.opacity = parseFloat(localStorage.getItem(Settings.Opacity) || '1');
          });
        }
        if (onObjectSelected) onObjectSelected(true, activeObjects.length === canvasEl.getObjects.length);
      })
      .on('selection:updated', opt => {
        const activeSelection = opt.target as fabric.ActiveSelection;
        Object.assign(activeSelection, getStitchingManipulationRulesFromActiveSelection(activeSelection));

        const activeObjects = canvasEl.getActiveObjects();

        populateGroupLockFields(activeObjects, activeSelection);

        if (activeObjects.length) {
          canvasEl.getObjects().forEach(element => {
            element.opacity = 1;
          });
          activeObjects.forEach(element => {
            element.opacity = parseFloat(localStorage.getItem(Settings.Opacity) || '1');
          });
        }
        if (onObjectSelected) onObjectSelected(activeObjects.length > 0, activeObjects.length === canvasEl.getObjects.length);
      })
      .on('selection:cleared', () => {
        if (canvasEl.getObjects()) {
          canvasEl.getObjects().forEach(element => {
            element.opacity = 1;
          });
        }
        if (onObjectSelected) onObjectSelected(false, false);
      });

    return () => {
      canvasEl
        .off('mouse:wheel')
        .off('mouse:down')
        .off('mouse:move')
        .off('mouse:up')
        .off('selection:created')
        .off('selection:updated')
        .off('selection:cleared')
        .off('object:modified');
    };
  }, [isPanning, selection, isDragging, lastPosX, lastPosY, canvasEl, objectModifiedHandler, onObjectSelected, onImageSelected]);
}

const populateGroupLockFields = (activeObjects: fabric.Object[], activeSelection: fabric.ActiveSelection) => {
  activeSelection.lockScalingX = activeObjects.some(x => x.lockScalingX);
  activeSelection.lockScalingY = activeObjects.some(x => x.lockScalingY);
  activeSelection.lockMovementX = activeObjects.some(x => x.lockMovementX);
  activeSelection.lockMovementY = activeObjects.some(x => x.lockMovementY);
  activeSelection.lockRotation = activeObjects.some(x => x.lockRotation);
  activeSelection.lockScalingFlip = true;

  const lockScale = activeSelection.lockScalingX || activeSelection.lockScalingY;

  activeSelection.setControlsVisibility({
    mt: false,
    mb: false,
    ml: false,
    mr: false,
    bl: !lockScale,
    br: !lockScale,
    tl: !lockScale,
    tr: !lockScale
  });
};

export default useCanvasEvents;
